import type { Column } from '@tanstack/react-table';

import { cn } from '@repo/lib/cn';
import { startOfDay, endOfDay, format } from 'date-fns';
import { ArrowRightIcon, PlusIcon, XIcon } from 'lucide-react';
import { startTransition, useMemo, useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ButtonGroup } from '@/components/ui/button';
import { DatePickerCalendar } from '@/components/ui/date-picker';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';

interface DateRangeFilterValue {
  start?: Date | string;
  end?: Date | string;
}

interface NormalizedDateRangeFilterValue {
  start?: Date;
  end?: Date;
}

interface TanstackTableDateRangeFilterProps<TData, TValue> {
  column?: Column<TData, TValue>;
  title?: string;
  onRemove?: () => void;
  onValueChange?: () => void;
}

/** Normalize a date value (string or Date) to a Date object */
const normalizeDate = (value: Date | string | undefined): Date | undefined => {
  if (!value) return undefined;
  return typeof value === 'string' ? new Date(value) : value;
};

export default function TanstackTableDateRangeFilter<TData, TValue>({ column, title, onRemove, onValueChange }: TanstackTableDateRangeFilterProps<TData, TValue>) {
  const [open, setOpen] = useState(false);
  const [startOpen, setStartOpen] = useState(false);
  const [endOpen, setEndOpen] = useState(false);

  // Use column's reactive getFilterValue() - TanStack Table handles reactivity
  const rawFilterValue = column?.getFilterValue() as DateRangeFilterValue | undefined;

  const filterValue = useMemo<NormalizedDateRangeFilterValue | undefined>(() => {
    if (!rawFilterValue) return undefined;
    return {
      start: normalizeDate(rawFilterValue.start),
      end: normalizeDate(rawFilterValue.end),
    };
  }, [rawFilterValue]);

  const hasFilter = !!(filterValue?.start || filterValue?.end);

  return (
    <Popover open={open} onOpenChange={setOpen} modal={false}>
      <ButtonGroup>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className={cn('bg-input hover:bg-input-focus focus-visible:bg-input-focus', !hasFilter && 'border-dashed')}>
            {!hasFilter && <PlusIcon />}
            <span className="truncate">{title}</span>
            <Badge variant={'muted'} size={'sm'} border={false} className={cn(!hasFilter && 'hidden', 'px-1 py-0.5 text-xs h-5')}>
              {filterValue?.start ? format(filterValue.start, 'dd.MM.yyyy') : '-'} <ArrowRightIcon className="text-muted-foreground size-2 shrink-0" />{' '}
              {filterValue?.end ? format(filterValue.end, 'dd.MM.yyyy') : '-'}
            </Badge>
          </Button>
        </PopoverTrigger>
        <Button
          variant="outline"
          size="sm"
          className={cn('bg-input hover:bg-input-focus focus-visible:bg-input-focus', !hasFilter && 'border-dashed')}
          onClick={() => {
            startTransition(() => {
              column?.setFilterValue(undefined);
            });
            setOpen(false);
            onRemove?.();
          }}
        >
          <XIcon />
        </Button>
      </ButtonGroup>

      <PopoverContent className="w-80 p-0">
        <div className="space-y-3">
          <div className={cn('group relative flex h-24 min-h-24 w-full items-stretch p-0')} data-both-filled={filterValue?.start && filterValue?.end ? 'true' : undefined}>
            <div className="flex h-full w-full items-stretch">
              {/* Start Date */}
              <Popover open={startOpen} onOpenChange={setStartOpen}>
                <PopoverTrigger asChild>
                  <button
                    data-state={!filterValue?.start ? 'empty' : 'filled'}
                    className={cn(
                      'peer/start flex flex-1 flex-col items-start justify-center px-4 transition-colors cursor-pointer select-none rounded-s-lg',
                      'bg-accent hover:bg-muted',
                      'data-[state=filled]:bg-popover data-[state=filled]:hover:bg-accent/75',
                      'transition-outline outline-focus-ring focus-visible:z-10 focus-visible:outline-3'
                    )}
                  >
                    <p className="text-muted-foreground text-[10px] font-bold tracking-wider uppercase">Od</p>
                    {filterValue?.start ? (
                      <p className="text-foreground text-sm leading-tight font-semibold">{format(filterValue?.start, 'LLL dd, y')}</p>
                    ) : (
                      <p className="text-muted-foreground text-sm font-medium">Nie wybrano</p>
                    )}
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto border-0 p-0" align="start" disablePortal>
                  <DatePickerCalendar
                    selected={filterValue?.start}
                    onSelect={(date) => {
                      setStartOpen(false);
                      const hadValue = !!(filterValue?.start || filterValue?.end);
                      // Mark as non-urgent - filtering is expensive
                      startTransition(() => {
                        if (filterValue?.end || date) {
                          column?.setFilterValue({
                            start: date ? startOfDay(date) : undefined,
                            end: filterValue?.end ? endOfDay(filterValue.end) : undefined,
                          });
                        } else {
                          column?.setFilterValue(undefined);
                        }
                      });
                      // Notify when filter gets a value for the first time
                      if (!hadValue && (date || filterValue?.end)) {
                        onValueChange?.();
                      }
                    }}
                    matcher={filterValue?.end ? { after: endOfDay(filterValue.end) } : undefined}
                  />
                </PopoverContent>
              </Popover>

              {/* Separator / Icon */}
              <div
                className={cn(
                  'flex items-center justify-center px-2 transition-colors [&_svg]:pointer-events-none',
                  'bg-accent hover:bg-muted group-hover:bg-muted',
                  'peer-hover/start:bg-muted peer-hover/end:bg-muted',
                  'group-data-[both-filled=true]:bg-popover group-data-[both-filled=true]:hover:bg-accent/75 group-data-[both-filled=true]:group-hover:bg-accent/75',
                  'group-data-[both-filled=true]:peer-hover/start:bg-accent/75 group-data-[both-filled=true]:peer-hover/end:bg-accent/75'
                )}
              >
                <ArrowRightIcon aria-hidden="true" className={cn('size-4 transition-colors', filterValue?.start && filterValue?.end ? 'text-foreground' : 'text-muted-foreground')} />
              </div>

              {/* End Date */}
              <Popover open={endOpen} onOpenChange={setEndOpen}>
                <PopoverTrigger asChild>
                  <button
                    data-state={!filterValue?.end ? 'empty' : 'filled'}
                    className={cn(
                      'peer/end flex flex-1 flex-col items-start justify-center px-4 transition-colors cursor-pointer select-none transition-outline outline-focus-ring',
                      'bg-accent hover:bg-muted',
                      'data-[state=filled]:bg-popover data-[state=filled]:hover:bg-accent/75',
                      'focus-visible:z-10 focus-visible:outline-3 rounded-e-lg'
                    )}
                  >
                    <p className="text-muted-foreground text-[10px] font-bold tracking-wider uppercase">Do</p>
                    {filterValue?.end ? (
                      <p className="text-foreground text-sm leading-tight font-semibold">{format(filterValue.end, 'LLL dd, y')}</p>
                    ) : (
                      <p className="text-muted-foreground text-sm font-medium">Nie wybrano</p>
                    )}
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto border-0 p-0" align="start" disablePortal>
                  <DatePickerCalendar
                    selected={filterValue?.end}
                    onSelect={(date) => {
                      setEndOpen(false);
                      const hadValue = !!(filterValue?.start || filterValue?.end);
                      // Mark as non-urgent - filtering is expensive
                      startTransition(() => {
                        if (filterValue?.start || date) {
                          column?.setFilterValue({
                            start: filterValue?.start ? startOfDay(filterValue.start) : undefined,
                            end: date ? endOfDay(date) : undefined,
                          });
                        } else {
                          column?.setFilterValue(undefined);
                        }
                      });
                      // Notify when filter gets a value for the first time
                      if (!hadValue && (date || filterValue?.start)) {
                        onValueChange?.();
                      }
                    }}
                    matcher={filterValue?.start ? { before: startOfDay(filterValue.start) } : undefined}
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>
        </div>
        {hasFilter && (
          <div className="p-2">
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="w-full"
              onClick={() => {
                startTransition(() => {
                  column?.setFilterValue(undefined);
                });
                setOpen(false);
                onRemove?.();
              }}
            >
              Wyczyść
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
